'use client';
import React, { useEffect, useState } from 'react';
import { Zap, CheckCircle, AlertTriangle, Phone, FileText, Star, Wrench, DollarSign, Shield } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface ActivityEvent {
  id: string;
  time: string;
  type: 'dispatch' | 'estimate' | 'warranty' | 'permit' | 'financing' | 'renewal' | 'emergency' | 'collection' | 'review';
  agent: string;
  message: string;
  value?: string;
}

const initialEvents: ActivityEvent[] = [
  { id: 'ae-001', time: '11:18 PM', type: 'emergency', agent: 'Emergency Intake AI', message: 'Priority-1 dispatch: No A/C — 108°F — Irvine, Nguyen residence', value: 'ETA 22 min' },
  { id: 'ae-002', time: '11:14 PM', type: 'estimate', agent: 'Estimate Recovery AI', message: 'Estimate #4821 accepted by Delgado, M. — Lennox XC21 5-ton replacement', value: '+$18,400' },
  { id: 'ae-003', time: '11:09 PM', type: 'warranty', agent: 'Warranty AI', message: 'Serial #LX2024-88412 verified — Carrier 3-ton — Rodriguez, A.', value: 'Registered' },
  { id: 'ae-004', time: '11:02 PM', type: 'permit', agent: 'Permit Tracker AI', message: 'Permit #P-2291 approved — Anaheim — Ready for install crew', value: 'Approved' },
  { id: 'ae-005', time: '10:58 PM', type: 'financing', agent: 'Financing AI', message: 'GoodLeap approval: Yamamoto, K. — $14,800 — 0% 18-month plan', value: '$14,800' },
  { id: 'ae-006', time: '10:51 PM', type: 'renewal', agent: 'Membership AI', message: '6 maintenance memberships auto-renewed via SMS campaign — Batch #44', value: '+$1,374' },
  { id: 'ae-007', time: '10:44 PM', type: 'collection', agent: 'AR Recovery AI', message: 'Payment collected — Martinez, C. — Invoice #8841 — 47 days outstanding', value: '$2,840' },
  { id: 'ae-008', time: '10:39 PM', type: 'dispatch', agent: 'Dispatcher Copilot', message: 'Route resequenced — Tech #7 (Torres) — 6 stops optimized, saved 38 min', value: '-38 min' },
  { id: 'ae-009', time: '10:31 PM', type: 'review', agent: 'Performance AI', message: 'Call review flagged — Tech #12 (Patel) — missed upsell on IAQ opportunity', value: 'Coaching' },
  { id: 'ae-010', time: '10:24 PM', type: 'estimate', agent: 'Estimate Recovery AI', message: 'Follow-up sequence triggered — Chen, B. — Estimate #4798 — $22,100 pending', value: 'Seq. 3 of 5' },
  { id: 'ae-011', time: '10:18 PM', type: 'emergency', agent: 'Emergency Intake AI', message: 'Priority-2 commercial dispatch — Office complex, Tustin — Compressor failure', value: 'ETA 45 min' },
  { id: 'ae-012', time: '10:09 PM', type: 'permit', agent: 'Permit Tracker AI', message: 'Permit correction submitted — Costa Mesa — Electrical spec update', value: 'Resubmitted' },
  { id: 'ae-013', time: '9:58 PM', type: 'financing', agent: 'Financing AI', message: 'Synchrony approval: Williams, T. — $9,200 — 6-month deferred interest', value: '$9,200' },
  { id: 'ae-014', time: '9:44 PM', type: 'renewal', agent: 'Membership AI', message: 'Dormant customer reactivated — Kim, S. — Last service 14 months ago', value: '+$229' },
  { id: 'ae-015', time: '9:32 PM', type: 'collection', agent: 'AR Recovery AI', message: 'Payment plan agreed — Johnson, R. — $4,200 over 3 installments', value: '$4,200' },
  { id: 'ae-016', time: '9:18 PM', type: 'dispatch', agent: 'Dispatcher Copilot', message: 'Weather surge: 4 additional techs scheduled for tomorrow 7AM shift', value: '+4 Techs' },
  { id: 'ae-017', time: '9:04 PM', type: 'warranty', agent: 'Warranty AI', message: '3 Trane systems registered — Batch warranty filing submitted to manufacturer', value: '3 Units' },
  { id: 'ae-018', time: '8:51 PM', type: 'estimate', agent: 'Estimate Recovery AI', message: 'Estimate #4744 recovered after 12 days — Park, L. — $16,800 replacement', value: '+$16,800' },
];

const typeConfig: Record<ActivityEvent['type'], { icon: React.ElementType; color: string; bg: string }> = {
  emergency: { icon: AlertTriangle, color: 'text-danger', bg: 'bg-danger/20' },
  estimate:  { icon: FileText,      color: 'text-success', bg: 'bg-success/20' },
  warranty:  { icon: Shield,        color: 'text-secondary', bg: 'bg-secondary/20' },
  permit:    { icon: CheckCircle,   color: 'text-accent', bg: 'bg-accent/20' },
  financing: { icon: DollarSign,    color: 'text-success', bg: 'bg-success/20' },
  renewal:   { icon: Star,          color: 'text-accent', bg: 'bg-accent/20' },
  collection:{ icon: DollarSign,    color: 'text-success', bg: 'bg-success/20' },
  dispatch:  { icon: Zap,           color: 'text-warning', bg: 'bg-warning/20' },
  review:    { icon: Wrench,        color: 'text-muted-foreground', bg: 'bg-muted' },
};

export default function ActivityFeed() {
  const [events, setEvents] = useState<ActivityEvent[]>(initialEvents);
  const [newCount, setNewCount] = useState(0);

  useEffect(() => {
    // Backend: connect to WebSocket or SSE endpoint for live activity events
    const liveEvents: ActivityEvent[] = [
      { id: 'ae-live-1', time: '', type: 'renewal', agent: 'Membership AI', message: 'Auto-renewal SMS sent — 12 customers in Batch #45', value: '+$2,748' },
      { id: 'ae-live-2', time: '', type: 'dispatch', agent: 'Dispatcher Copilot', message: 'Morning route plan generated — 14 technicians, 89 stops scheduled', value: '7:00 AM' },
    ];

    const addEvent = () => {
      const now = new Date();
      const h = now.getHours() % 12 || 12;
      const m = now.getMinutes().toString().padStart(2, '0');
      const ampm = now.getHours() >= 12 ? 'PM' : 'AM';
      const timeStr = `${h}:${m} ${ampm}`;
      const pick = liveEvents[Math.floor(newCount % liveEvents.length)];
      const newEvent = { ...pick, time: timeStr, id: `ae-live-${Date.now()}` };
      setEvents(prev => [newEvent, ...prev.slice(0, 24)]);
      setNewCount(c => c + 1);
    };

    const t = setInterval(addEvent, 18000);
    return () => clearInterval(t);
  }, [newCount]);

  return (
    <div className="activity-bg rounded-lg border border-white/10 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-success pulse-dot" />
            <h3 className="text-white font-semibold text-base">Live AI Activity Feed</h3>
          </div>
          <span className="bg-white/10 text-white/70 text-xs px-2 py-0.5 rounded-full font-medium">
            {events.length} events today
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Phone size={14} className="text-white/40" />
          <span className="text-white/40 text-xs">All 18 agents reporting</span>
        </div>
      </div>

      {/* Events */}
      <div className="divide-y divide-white/5 max-h-96 overflow-y-auto scrollbar-thin">
        {events.map((event) => {
          const cfg = typeConfig[event.type];
          const Icon = cfg.icon;
          return (
            <div key={event.id} className="px-6 py-3 hover:bg-white/5 transition-colors flex items-start gap-4 fade-in">
              <div className={`w-7 h-7 rounded-md ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <Icon size={13} className={cfg.color} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <span className="text-white/50 text-xs mr-2 font-medium">{event.agent}</span>
                    <span className="text-white/85 text-xs leading-relaxed">{event.message}</span>
                  </div>
                  {event.value && (
                    <span className="text-xs font-semibold text-success whitespace-nowrap font-tabular flex-shrink-0">
                      {event.value}
                    </span>
                  )}
                </div>
              </div>
              <span className="text-white/30 text-xs font-tabular whitespace-nowrap flex-shrink-0">{event.time}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}